import {
    ArrowClockwise,
    ChartLineUp,
    CurrencyDollar,
    Lightning,
    MagnifyingGlass,
    Rocket,
    ShieldCheck,
    ShoppingCart,
    Storefront,
    Tag,
} from 'shared/icons';
import { FormatDateShort } from '../../../utils/date';
import { FormatInt, FormatMoney } from '../../../utils/format';

/**
 * Blocos compartilhados das abas "Legacy" (cópia do Campaign Manager /
 * meli_manager usado pela diretoria). A disposição dos elementos replica
 * campaigns-v2.html: stats (6 cards) → busca → pills → contagem → cards
 * expansíveis. Fórmulas idênticas às do legado (fórmula v2):
 *   rebate = original × meli% / 100 ; líquido = consumer + rebate.
 */

// bi-* do legado -> nossos ícones
export const TYPE_ICONS = {
    DEAL: Tag,
    SMART: ChartLineUp,
    PRE_NEGOTIATED: ShieldCheck,
    SELLER_CAMPAIGN: Storefront,
    SELLER_COUPON_CAMPAIGN: CurrencyDollar,
    LIGHTNING: Lightning,
    PRICE_MATCHING: ArrowClockwise,
    DOD: Rocket,
    MARKETPLACE_CAMPAIGN: ShoppingCart,
};

// cores por tipo (borda/texto do ícone), espelhando o CSS do legado
export const TYPE_COLORS = {
    DEAL: 'text-red-500 border-red-500',
    SMART: 'text-blue-500 border-blue-500',
    PRE_NEGOTIATED: 'text-green-500 border-green-500',
    SELLER_CAMPAIGN: 'text-orange-500 border-orange-500',
    SELLER_COUPON_CAMPAIGN: 'text-purple-500 border-purple-500',
    LIGHTNING: 'text-yellow-500 border-yellow-500',
    PRICE_MATCHING: 'text-cyan-500 border-cyan-500',
    DOD: 'text-red-400 border-red-400',
    MARKETPLACE_CAMPAIGN: 'text-blue-500 border-blue-500',
};

// Formatadores: delegam pros utils compartilhados (mesmos guards de antes).
export const FmtMoney = (v) => FormatMoney(v);
export const FmtInt = (v) => FormatInt(v || 0);
export const FmtDate = (v) => FormatDateShort(v) || '—';

export const IsProductActive = (status) => status === 'active';

// Stat card neutro (sem acentos de borda). Radius padrão: card = rounded-xl.
export const LegacyStatCard = ({ label, value }) => (
    <div className="rounded-xl border border-mono-200 bg-white p-3 dark:border-mono-800 dark:bg-mono-950">
        <p className="text-[10px] uppercase tracking-wide text-mono-500 leading-tight">{label}</p>
        <p className="mt-0.5 text-xl font-bold text-black dark:text-white tabular-nums">{value}</p>
    </div>
);

export const LegacySearchBox = ({ value, onChange, placeholder }) => (
    <div className="relative">
        <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-mono-400" />
        <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder={placeholder}
            className="w-full rounded-lg border border-mono-200 bg-white py-2.5 pl-10 pr-3 text-sm text-black outline-none focus:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-white dark:focus:border-mono-600"
        />
    </div>
);

export const LegacyFilterPills = ({ pills, active, onSelect }) => (
    <div className="flex gap-2 overflow-x-auto pb-1">
        {pills.map((pill) => (
            <button
                key={pill.id}
                type="button"
                onClick={() => onSelect(pill.id)}
                className={`flex shrink-0 items-center gap-1.5 rounded-full border px-3.5 py-1.5 text-[13px] transition-colors ${
                    active === pill.id
                        ? 'border-black bg-black text-white dark:border-white dark:bg-white dark:text-black'
                        : 'border-mono-200 bg-white text-mono-600 hover:border-mono-400 dark:border-mono-800 dark:bg-mono-950 dark:text-mono-400'
                }`}
            >
                {pill.label}
                <span
                    className={`rounded-md px-1.5 py-px text-[11px] font-semibold ${
                        active === pill.id
                            ? 'bg-white/20 dark:bg-black/20'
                            : 'bg-mono-100 dark:bg-mono-800'
                    }`}
                >
                    {pill.count}
                </span>
            </button>
        ))}
    </div>
);

// pill de status (started/pending/finished/paused/candidate) — cores do legado
export const LegacyStatusPill = ({ status }) => {
    const map = {
        started: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
        pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
        candidate: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300',
        finished: 'bg-mono-100 text-mono-600 dark:bg-mono-800 dark:text-mono-300',
        paused: 'bg-orange-100 text-orange-700 dark:bg-orange-900/40 dark:text-orange-300',
        active: 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300',
    };
    return (
        <span
            className={`inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase ${map[status] || map.finished}`}
        >
            {status || '-'}
        </span>
    );
};

// seção do rodapé dos cards (label pequeno + valor), como .camp-section
export const LegacySection = ({ label, children, sub }) => (
    <div className="flex min-h-[56px] flex-col justify-center border-r border-mono-100 px-2.5 py-2 last:border-r-0 dark:border-mono-900">
        <p className="mb-0.5 text-[8px] uppercase tracking-wide text-mono-400">{label}</p>
        <div className="text-sm font-bold leading-tight tabular-nums text-black dark:text-white">{children}</div>
        {sub ? <p className="mt-px text-[9px] text-mono-400">{sub}</p> : null}
    </div>
);

export const LegacyEmptyState = ({ icon: Icon, text }) => (
    <div className="rounded-xl border border-mono-200 bg-white p-12 text-center dark:border-mono-800 dark:bg-mono-950">
        {Icon ? <Icon size={44} className="mx-auto mb-3 text-mono-300 dark:text-mono-700" /> : null}
        <p className="text-mono-500">{text}</p>
    </div>
);

export const LegacyLoading = ({ text }) => (
    <div className="rounded-xl border border-mono-200 bg-white p-12 text-center dark:border-mono-800 dark:bg-mono-950">
        <div className="mx-auto mb-4 h-9 w-9 animate-spin rounded-full border-[3px] border-mono-200 border-t-black dark:border-mono-800 dark:border-t-white" />
        <p className="text-sm text-mono-500">{text}</p>
    </div>
);

// Toggle global "Incluir inativos" — mesma posição/semântica do legado
export const LegacyInactiveToggle = ({ includeInactive, onChange }) => (
    <div className="flex flex-wrap items-center gap-3 text-xs text-mono-500">
        <span className="inline-flex items-center gap-1.5">
            Filtrando apenas{' '}
            <b className={includeInactive ? 'text-orange-500' : 'text-green-600 dark:text-green-400'}>
                {includeInactive ? 'todos os anúncios' : 'anúncios ativos'}
            </b>
        </span>
        <button
            type="button"
            role="switch"
            aria-checked={includeInactive}
            onClick={() => onChange(!includeInactive)}
            className={`relative h-5 w-9 rounded-full border transition-colors ${
                includeInactive
                    ? 'border-black bg-black dark:border-white dark:bg-white'
                    : 'border-mono-300 bg-mono-100 dark:border-mono-700 dark:bg-mono-900'
            }`}
            title="Incluir anúncios pausados/encerrados"
        >
            <span
                className={`absolute top-0.5 h-3.5 w-3.5 rounded-full transition-transform ${
                    includeInactive
                        ? 'translate-x-[18px] bg-white dark:bg-black'
                        : 'translate-x-0.5 bg-mono-400'
                }`}
            />
        </button>
        <span>Incluir inativos</span>
    </div>
);

export const Unwrap = (res) => res?.data ?? res;
